import { useEffect, useCallback, useRef } from 'react';
import L from 'leaflet';

export const usePointSynchronization = (
  mapRefs, 
  trackData, 
  currentVehicle, 
  containerId, 
  tabId, 
  formatDate, 
  formatTime
) => {
  const { mapInstanceRef, animatedMarkerRef } = mapRefs;
  const lastSelectedPointRef = useRef({
    index: -1,
    timestamp: null,
    lastUpdateTime: 0
  });

  // Функция ручной синхронизации с графиками (объявляем первой)
  const handleManualSync = useCallback((pointIndex) => {
    console.log(`TrackReport: Ручная синхронизация точки #${pointIndex}`);
    
    if (!trackData || pointIndex < 0 || pointIndex >= trackData.length) {
      console.warn('TrackReport: Некорректный индекс точки для синхронизации:', pointIndex);
      return;
    }
    
    try {
      const pointData = trackData[pointIndex];
      const currentSyncGroup = tabId ? `sync-chart-container-tab-${tabId}` : '*';
      
      console.log(`TrackReport: Ручная синхронизация точки с группой ${currentSyncGroup}`, pointData);
      
      // 1. Создаем событие с данными точки
      const pointEvent = new CustomEvent('pointSelected', {
        detail: {
          timestamp: pointData.timestamp,
          latitude: pointData.latitude,
          longitude: pointData.longitude,
          speed: pointData.speed,
          source: containerId || 'track-report',
          syncGroup: currentSyncGroup,
          index: pointIndex,
          totalPoints: trackData.length
        }
      });
      
      // 2. Отправляем событие через DOM
      document.dispatchEvent(pointEvent);
      console.log('TrackReport: Отправлено событие pointSelected через DOM');
      
      // 3. Если доступен ChartSyncManager, используем его метод notifyPointSelected
      try {
        if (window.ChartSyncManager && typeof window.ChartSyncManager.notifyPointSelected === 'function') {
          const chartPointData = {
            index: pointIndex,
            point: pointData,
            totalPoints: trackData.length,
            timestamp: pointData.timestamp,
            sourceId: containerId || 'track-report',
            syncGroupId: currentSyncGroup,
            label: pointData.timestamp,
            value: pointData.speed,
            reportType: 'trackreport',
            priority: 'high'
          };
          
          console.log('TrackReport: Используем ChartSyncManager.notifyPointSelected для синхронизации');
          window.ChartSyncManager.notifyPointSelected(chartPointData);
          
          // Проверяем, сколько контейнеров в группе
          if (typeof window.ChartSyncManager.getGroupContainers === 'function') {
            const containers = window.ChartSyncManager.getGroupContainers(currentSyncGroup);
            console.log(`ChartSyncManager: Найдено ${containers ? containers.length : 0} контейнеров в группе ${currentSyncGroup}`);
          }
        }
      } catch (syncError) {
        console.error('TrackReport: Ошибка при использовании ChartSyncManager:', syncError);
      }
      
      // 4. Отправляем событие напрямую в canvas элементы графиков
      setTimeout(() => {
        try {
          const canvasElements = document.querySelectorAll('canvas[data-graph="true"], canvas.chartjs-render-monitor');
          
          if (canvasElements.length > 0) {
            console.log(`TrackReport: Найдено ${canvasElements.length} canvas-элементов графиков для прямой синхронизации`);
            
            const chartPointData = {
              index: pointIndex,
              point: pointData,
              totalPoints: trackData.length,
              timestamp: pointData.timestamp,
              sourceId: containerId || 'track-report',
              syncGroupId: currentSyncGroup,
              label: pointData.timestamp,
              value: pointData.speed,
              reportType: 'trackreport',
              priority: 'high'
            };
            
            canvasElements.forEach(canvas => {
              const event = new CustomEvent('directPointSelected', {
                detail: chartPointData
              });
              canvas.dispatchEvent(event);
            });
          }
        } catch (canvasError) {
          console.error('TrackReport: Ошибка при прямой синхронизации с canvas элементами:', canvasError);
        }
      }, 100);
      
      // 5. Дополнительно отправляем событие chartPointSelected (как в TrackMap)
      setTimeout(() => {
        try {
          // Создаем данные точки в формате BaseChart (как в TrackMap)
          const chartPointData = {
            sourceContainerId: containerId || 'track-report',
            timestamp: new Date(pointData.timestamp).getTime(),
            pointIndex: pointIndex,
            datasetIndex: 0,
            label: pointData.timestamp,
            value: pointData.speed,
            reportType: 'trackreport',
            priority: 'high',
            syncGroupId: currentSyncGroup
          };
          
          // Создаем и отправляем событие точно такое же, какое ожидает BaseChart
          const chartEvent = new CustomEvent('chartPointSelected', {
            detail: chartPointData,
            bubbles: false
          });
          
          document.dispatchEvent(chartEvent);
          console.log('TrackReport: Отправлено событие chartPointSelected при выделении точки', chartPointData);
        } catch (chartError) {
          console.error('TrackReport: Ошибка при отправке события chartPointSelected:', chartError);
        }
      }, 50);
    } catch (error) {
      console.error('TrackReport: Ошибка при ручной синхронизации:', error);
    }
  }, [trackData, containerId, tabId]);

  // Функция подсветки точки трека (теперь handleManualSync доступен)
  const highlightTrackPoint = useCallback((pointIndex) => {
    try {
      // Проверяем наличие данных и валидность индекса
      if (!trackData || !trackData.length || pointIndex < 0 || pointIndex >= trackData.length) {
        console.warn('TrackReport: Некорректный индекс точки для подсветки', pointIndex);
        return;
      }
      
      const point = trackData[pointIndex];
      console.log(`TrackReport: Подсветка точки трека с индексом ${pointIndex}`, point);
      
      // Проверяем наличие координат
      if (!point.latitude || !point.longitude) {
        console.warn('TrackReport: Точка не содержит координат', point);
        return;
      }
      
      // Перемещаем карту к выбранной точке
      if (mapInstanceRef.current) {
        mapInstanceRef.current.setView([point.latitude, point.longitude], mapInstanceRef.current.getZoom());
      }
      
      // Перемещаем анимированный маркер к выбранной точке
      if (animatedMarkerRef.current) {
        // Устанавливаем новые координаты
        animatedMarkerRef.current.setLatLng([point.latitude, point.longitude]);
        
        // Сохраняем текущий индекс точки в маркере
        animatedMarkerRef.current._currentPointIndex = pointIndex;
        
        // Обновляем всплывающее окно с информацией о точке
        const popupContent = `
          <div class="map-popup">
            <h4>${currentVehicle?.name || 'Транспорт'}</h4>
            <div class="map-popup-details">
              <p><strong>Дата:</strong> ${formatDate(point.timestamp)}</p>
              <p><strong>Время:</strong> ${formatTime(point.timestamp)}</p>
              <p><strong>Скорость:</strong> ${point.speed} км/ч</p>
              ${point.altitude ? `<p><strong>Высота:</strong> ${point.altitude} м</p>` : ''}
              ${point.course ? `<p><strong>Курс:</strong> ${point.course}°</p>` : ''}
              ${point.satellites ? `<p><strong>Спутники:</strong> ${point.satellites}</p>` : ''}
            </div>
          </div>
        `;
        
        animatedMarkerRef.current.setPopupContent(popupContent);
        
        // Открываем всплывающее окно
        animatedMarkerRef.current.openPopup();
      }
      
      // Визуально выделяем точку на треке
      if (mapInstanceRef.current) {
        // Удаляем предыдущий круг, если он существует
        if (window.selectedPointCircle) {
          mapInstanceRef.current.removeLayer(window.selectedPointCircle);
        }
        
        // Создаем новый круг в месте выбранной точки
        window.selectedPointCircle = L.circleMarker([point.latitude, point.longitude], {
          radius: 8,
          color: '#FF4500',
          fillColor: '#FFA500',
          fillOpacity: 0.7,
          weight: 2
        }).addTo(mapInstanceRef.current);
      }
      
      // Синхронизируем с графиками через handleManualSync
      setTimeout(() => {
        try {
          handleManualSync(pointIndex);
        } catch (err) {
          console.error('TrackReport: Ошибка при вызове handleManualSync из highlightTrackPoint:', err);
        }
      }, 0);
    } catch (error) {
      console.error('TrackReport: Ошибка при подсветке точки трека:', error);
    }
  }, [trackData, currentVehicle, formatDate, formatTime, mapInstanceRef, animatedMarkerRef, handleManualSync]);

  // Обработчик событий выбора точки (копия из TrackMap)
  const handlePointSelected = useCallback((event) => {
    try {
      const { timestamp, source, index } = event.detail;
      
      // Текущее время для дебаунсинга
      const currentTime = Date.now();
      
      // Игнорируем события от самой карты
      if (source === 'track-report' || source === containerId) {
        console.log('TrackReport: Игнорируем событие от самой карты', source);
        return;
      }
      
      const lastSelectedPoint = lastSelectedPointRef.current;
      
      // Защита от зацикливания - если получаем много событий за короткий промежуток
      if (currentTime - lastSelectedPoint.lastUpdateTime < 500) {
        console.log('TrackReport: Игнорируем частые события (дебаунсинг)', {
          lastUpdateTime: lastSelectedPoint.lastUpdateTime,
          currentTime: currentTime,
          diff: currentTime - lastSelectedPoint.lastUpdateTime
        });
        return;
      }
      
      // Проверка на странную дату из будущего
      const eventDate = new Date(timestamp);
      const now = new Date();
      if (eventDate > now) {
        console.warn('TrackReport: Игнорируем событие с датой из будущего', {
          timestamp,
          currentDate: now.toISOString()
        });
        return;
      }
      
      console.log('TrackReport: Получено событие выбора точки:', {
        timestamp: timestamp ? new Date(timestamp).toISOString() : null,
        source,
        index
      });
      
      if (!trackData || trackData.length === 0) {
        console.warn('TrackReport: Невозможно выбрать точку - данные трека отсутствуют');
        return;
      }
      
      // Если есть прямой индекс, используем его
      if (typeof index === 'number' && index >= 0 && index < trackData.length) {
        console.log(`TrackReport: Используем прямой индекс точки: ${index}`);
        
        // Проверяем, не является ли это повторным выбором той же точки
        if (index === lastSelectedPoint.index && 
            currentTime - lastSelectedPoint.lastUpdateTime < 2000) {
          console.log('TrackReport: Игнорируем повторный выбор той же точки', index);
          return;
        }
        
        // Обновляем информацию о последней выбранной точке
        lastSelectedPointRef.current = {
          index,
          timestamp: trackData[index].timestamp,
          lastUpdateTime: currentTime
        };
        
        highlightTrackPoint(index);
        return;
      }
      
      // Если нет индекса, но есть timestamp, находим ближайшую точку трека ко времени
      if (timestamp) {
        const timestampDate = new Date(timestamp);
        let closestPoint = null;
        let minTimeDiff = Infinity;
        let closestIndex = -1;

        trackData.forEach((point, index) => {
          const pointTime = new Date(point.timestamp);
          const timeDiff = Math.abs(pointTime.getTime() - timestampDate.getTime());
          
          if (timeDiff < minTimeDiff) {
            minTimeDiff = timeDiff;
            closestPoint = point;
            closestIndex = index;
          }
        });
        
        if (closestPoint) {
          console.log('TrackReport: Найдена ближайшая точка трека:', {
            time: new Date(closestPoint.timestamp).toISOString(),
            diff: minTimeDiff / 1000,
            index: closestIndex
          });
          
          // Проверяем, не является ли это повторным выбором той же точки
          if (closestIndex === lastSelectedPoint.index && 
              currentTime - lastSelectedPoint.lastUpdateTime < 2000) {
            console.log('TrackReport: Игнорируем повторный выбор той же точки по времени', closestIndex);
            return;
          }
          
          // Обновляем информацию о последней выбранной точке
          lastSelectedPointRef.current = {
            index: closestIndex,
            timestamp: closestPoint.timestamp,
            lastUpdateTime: currentTime
          };
          
          highlightTrackPoint(closestIndex);
        }
      }
    } catch (error) {
      console.error('TrackReport: Ошибка при обработке события выбора точки:', error);
    }
  }, [trackData, containerId, highlightTrackPoint]);

  // Регистрация обработчиков событий выбора точки
  useEffect(() => {
    console.log('TrackReport: Регистрация обработчиков событий выбора точки');
    
    // Регистрируем обработчики событий
    document.addEventListener('pointSelected', handlePointSelected);
    document.addEventListener('chartPointSelected', handlePointSelected);
    
    // Очищаем обработчики при размонтировании
    return () => {
      console.log('TrackReport: Отмена регистрации обработчиков событий выбора точки');
      document.removeEventListener('pointSelected', handlePointSelected);
      document.removeEventListener('chartPointSelected', handlePointSelected);
    };
  }, [handlePointSelected]);

  // Регистрация в ChartSyncManager (как в TrackMap)
  useEffect(() => {
    // Идентификатор контейнера для синхронизации с графиками
    const syncContainerId = containerId;
    const currentSyncGroup = tabId ? `sync-chart-container-tab-${tabId}` : '*';
    
    // Добавляем задержку для уверенности, что ChartSyncManager загрузился
    setTimeout(() => {
      try {
        console.log('TrackReport: Доступные глобальные переменные:', 
          typeof window.ChartSyncManager);
        
        // Проверяем наличие ChartSyncManager глобально
        const syncManager = window.ChartSyncManager;
        
        // Выводим подробную информацию о syncManager
        if (syncManager) {
          console.log('TrackReport: SyncManager найден, методы:', 
            Object.keys(syncManager), 
            'тип registerContainer:', 
            typeof syncManager.registerContainer);
        }
        
        if (syncManager && typeof syncManager.registerContainer === 'function') {
          console.log(`TrackReport: Регистрация в ChartSyncManager, контейнер ${syncContainerId}, группа ${currentSyncGroup}`);
          
          try {
            // Регистрируем для конкретной группы синхронизации и логируем результат
            const result = syncManager.registerContainer(syncContainerId, 'trackmap', currentSyncGroup);
            console.log('TrackReport: Результат регистрации контейнера:', result);
            
            // Если у нас есть данные трека, сообщим ChartSyncManager о количестве точек
            if (trackData && trackData.length > 0) {
              if (typeof syncManager.setContainerDataLength === 'function') {
                syncManager.setContainerDataLength(syncContainerId, trackData.length);
                console.log(`TrackReport: Установлена длина данных ${trackData.length} для контейнера ${syncContainerId}`);
              }
            }
          } catch (registerError) {
            console.error('TrackReport: Ошибка при вызове registerContainer:', registerError);
          }
        } else {
          console.error('TrackReport: ChartSyncManager недоступен или не имеет метода registerContainer', 
            syncManager, 
            'registerContainer существует:', 
            syncManager && 'registerContainer' in syncManager);
          
          // Если метод есть, но это не функция, выводим его значение
          if (syncManager && 'registerContainer' in syncManager && typeof syncManager.registerContainer !== 'function') {
            console.log('TrackReport: registerContainer существует, но не является функцией:', syncManager.registerContainer);
          }
          
          // Попытка использовать альтернативный способ регистрации
          if (syncManager && typeof syncManager.register === 'function') {
            try {
              console.log('TrackReport: Попытка использовать метод register');
              syncManager.register(syncContainerId, 'trackmap', currentSyncGroup);
              console.log(`TrackReport: Контейнер ${syncContainerId} зарегистрирован через метод register`);
            } catch (altRegisterError) {
              console.error('TrackReport: Ошибка при использовании метода register:', altRegisterError);
            }
          }
          
          // Попытка инициализировать ChartSyncManager, если он недоступен
          if (typeof window.initChartSyncManager === 'function') {
            console.log('TrackReport: Попытка инициализировать ChartSyncManager');
            window.initChartSyncManager();
            
            // Повторная попытка регистрации после инициализации
            setTimeout(() => {
              const reloadedManager = window.ChartSyncManager;
              if (reloadedManager && typeof reloadedManager.registerContainer === 'function') {
                try {
                  reloadedManager.registerContainer(syncContainerId, 'trackmap', currentSyncGroup);
                  console.log('TrackReport: Повторная регистрация в ChartSyncManager выполнена');
                } catch (retryError) {
                  console.error('TrackReport: Ошибка при повторной регистрации:', retryError);
                }
              }
            }, 1000);
          }
        }
      } catch (error) {
        console.error('TrackReport: Ошибка при регистрации в ChartSyncManager:', error);
      }
    }, 1000);
    
    // Очистка при размонтировании
    return () => {
      try {
        const syncManager = window.ChartSyncManager;
        if (syncManager && typeof syncManager.unregisterContainer === 'function') {
          syncManager.unregisterContainer(syncContainerId);
          console.log(`TrackReport: Контейнер ${syncContainerId} отключен от ChartSyncManager`);
        }
      } catch (error) {
        console.error('TrackReport: Ошибка при отключении от ChartSyncManager:', error);
      }
    };
  }, [containerId, tabId, trackData]);

  // Обработка прямых событий от графиков
  useEffect(() => {
    const handleDirectPointSelected = (event) => {
      try {
        const detail = event.detail;
        console.log('TrackReport: Получено прямое событие от графика:', detail);
        
        // Если источник - текущий контейнер, игнорируем
        if (detail.sourceId === containerId) {
          console.log('TrackReport: Игнорируем событие от самой карты');
          return;
        }
        
        // Если есть метка времени, ищем соответствующую точку по времени
        if (detail.timestamp && trackData && trackData.length > 0) {
          const targetTime = typeof detail.timestamp === 'string' ? 
            new Date(detail.timestamp) : 
            detail.timestamp instanceof Date ? 
              detail.timestamp : 
              new Date(detail.timestamp);
          
          let closestIndex = -1;
          let minDiff = Infinity;
          
          trackData.forEach((point, index) => {
            const pointTime = typeof point.timestamp === 'string' ? 
              new Date(point.timestamp) : 
              point.timestamp instanceof Date ? 
                point.timestamp : 
                new Date(point.timestamp);
            
            const diff = Math.abs(pointTime - targetTime);
            if (diff < minDiff) {
              minDiff = diff;
              closestIndex = index;
            }
          });
          
          if (closestIndex >= 0) {
            console.log(`TrackReport: Найдена точка по времени, индекс: ${closestIndex}, разница: ${minDiff}ms`);
            highlightTrackPoint(closestIndex);
            
            // Центрируем карту на выбранной точке
            if (mapInstanceRef.current) {
              const point = trackData[closestIndex];
              const latLng = L.latLng(point.latitude, point.longitude);
              mapInstanceRef.current.setView(latLng, mapInstanceRef.current.getZoom());
            }
            return;
          }
        }
        
        // Если есть индекс, используем его напрямую
        if (typeof detail.index === 'number' && trackData && detail.index >= 0 && detail.index < trackData.length) {
          console.log(`TrackReport: Используем прямой индекс: ${detail.index}`);
          highlightTrackPoint(detail.index);
          
          // Центрируем карту на выбранной точке
          if (mapInstanceRef.current) {
            const point = trackData[detail.index];
            const latLng = L.latLng(point.latitude, point.longitude);
            mapInstanceRef.current.setView(latLng, mapInstanceRef.current.getZoom());
          }
        }
      } catch (error) {
        console.error('TrackReport: Ошибка при обработке прямого события от графика:', error);
      }
    };
    
    // Добавляем глобальный обработчик для прямых событий
    document.addEventListener('directPointSelected', handleDirectPointSelected);
    
    // Очистка при размонтировании
    return () => {
      document.removeEventListener('directPointSelected', handleDirectPointSelected);
    };
  }, [trackData, containerId, highlightTrackPoint, mapInstanceRef]);

  return {
    highlightTrackPoint,
    handleManualSync,
    handlePointSelected
  };
}; 